import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import DocTermsAndConditions from "./privacyTerms/DocTerms&Condition";
import { toast } from "react-toastify";
import { DocPresurvey } from "./ui/DocPresurvey";
import AadhaarVerificationPopup from "./AadhaarVerificationPopup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
const logo = process.env.PUBLIC_URL + "/images/logo.png";

const PediatricianSignup = ({ handleSignup }) => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    email: "",
    firstName: "",
    lastName: "",
    clinicName: "",
    specialization: "",
    pedRegistrationNo: "",
    s1: "",
    s2: "",
    s3: "",
    s4: "",
    s5: "",
  });

  const [error, setError] = useState("");
  const [orgsList, setOrgsList] = useState([]);
  const [isOrgSearchModalVisible, setIsOrgSearchModalVisible] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [termsAndConditionCheck, setTermsAndConditionCheck] = useState(false);

  // Modals state
  const [openPopup, setOpenPopup] = useState(false);
  const [preSurvey, setPreSurvey] = useState(false);

  // NEW: Health worker verification state
  const [isVerifyModalVisible, setIsVerifyModalVisible] = useState(false);
  const [docVerificationData, setDocVerificationData] = useState(null);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  // Derive if survey is completed based on presence of s1
  const docSurveyCompleted = !!formData.s1;

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setOrgLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/organizations`,
        );
        const data = await response.data.organizations;
        if (Array.isArray(data)) {
          setOrgsList(data);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      } finally {
        setOrgLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  // Intercept Presurvey completion to trigger Aadhaar for Health Workers
  const handleSurveyCompletion = (data) => {
    setFormData(data); // set survey data
    setPreSurvey(false);

    if (formData.specialization === "health_worker") {
      setTimeout(() => {
        setIsVerifyModalVisible(true);
      }, 200);
    } else {
      setTermsAndConditionCheck(true);
    }
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    if (
      formData.specialization === "health_worker" &&
      docSurveyCompleted &&
      !docVerificationData
    ) {
      // Recovery mechanism if they cancelled verification
      setIsVerifyModalVisible(true);
    } else {
      setOpenPopup(true);
    }
  };

  const handleSearchOrg = (text) => {
    setOrgSearchQuery(text);
    if (text.trim().length > 0) {
      const filtered = orgsList.filter((item) =>
        item.toLowerCase().includes(text.trim().toLowerCase()),
      );
      setFilteredOrgs(filtered);
    } else {
      setFilteredOrgs([]);
    }
  };

  const selectOrganization = (orgName) => {
    setFormData((prev) => ({ ...prev, clinicName: orgName.trim() }));
    setIsOrgSearchModalVisible(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      const phoneNumberConversion = Number(value);
      if (isNaN(phoneNumberConversion)) {
        return toast.warn("Please enter only numbers");
      }
    }

    if (name === "specialization") {
      // Reset survey & terms if specialization changes
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        s1: "",
        s2: "",
        s3: "",
        s4: "",
        s5: "",
        pedRegistrationNo: "",
      }));
      setTermsAndConditionCheck(false);
      setDocVerificationData(null);
    } else if (name === "clinicName" || name === "firstName") {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    } else if (name === "email") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value.toLowerCase().trim(),
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value.trim() }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "password",
      "confirmPassword",
      "phoneNumber",
      "email",
      "firstName",
      "lastName",
      "clinicName",
      "specialization",
      "s1",
    ];
    const isAnyFieldEmpty = requiredFields.some((field) => {
      const value = formData[field];
      if (typeof value === "string") return value.trim() === "";
      return value === null || value === undefined || value === "";
    });

    if (isAnyFieldEmpty) {
      return toast.warn(
        "Please fill all required fields, complete the terms, and take the survey.",
      );
    }

    if (
      formData.specialization === "pediatrician" &&
      formData.pedRegistrationNo.trim().length !== 10
    ) {
      return toast.warn(
        "10 Character Registration Number is required for pediatricians.",
      );
    }

    if (formData.specialization === "health_worker" && !docVerificationData) {
      return toast.warn(
        "Health Workers must complete the Identity Verification step.",
      );
    }

    const validateEmail = (email) => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zAZ0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(email.trim());
    };

    if (formData.email && !validateEmail(formData.email)) {
      return toast.warn("Please provide a valid email address");
    }
    const isValidPhone = /^\d{10}$/.test(formData.phoneNumber);
    if (!isValidPhone) {
      return toast.warn("Please enter 10 digit phone number");
    }
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{8,16}$/;
    if (!passwordRegex.test(formData.password)) {
      return toast.warn(
        "Password must be 8-16 characters long and include at least one uppercase letter, one lowercase letter, one digit, and one special character.",
      );
    }
    if (formData.password !== formData.confirmPassword) {
      return toast.warn("Passwords do not match");
    }

    try {
      // Base transformed data
      const transformedData = {
        password: formData.password,
        phone_number: formData.phoneNumber,
        email: formData.email,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName,
        clinic_name: formData.clinicName.trim(),
        specialization: formData.specialization,
        s1: formData.s1,
        s2: formData.s2,
        s3: formData.s3,
        s4: formData.s4,
        s5: formData.s5,
        user_type: "pediatrician",
      };

      // Append specific role data (Mirroring Expo Payload exactly)
      if (formData.specialization === "pediatrician") {
        transformedData.ped_registration_no = formData.pedRegistrationNo.trim();
      } else if (formData.specialization === "health_worker") {
        transformedData.aadhaar_number = docVerificationData.aadhaar_number;
        transformedData.request_id =
          docVerificationData.transactionReceipt.trim();
        transformedData.consent_mechanism = "AADHAAR_OTP";
      }

      const response = await handleSignup(transformedData);

      if (!response.success) {
        return toast.error(response.message);
      }
    } catch (error) {
      setError("Signup failed. Please try again.");
    }
  };

  return (
    <div className="signup-container">
      {(openPopup || preSurvey) && (
        <div key="global-overlay" className="term-privacy-overlay"></div>
      )}

      {openPopup && (
        <div className="term-privacy-popup">
          <DocTermsAndConditions
            setOpenPopup={setOpenPopup}
            setTermsAndConditionCheck={setTermsAndConditionCheck}
            setPreSurvey={setPreSurvey}
          />
        </div>
      )}

      {preSurvey && (
        <div className="term-privacy-popup" style={{ maxWidth: "800px" }}>
          <DocPresurvey
            setPreSurvey={setPreSurvey}
            setTermsAndConditionCheck={setTermsAndConditionCheck}
            setFormData={handleSurveyCompletion} // Using wrapper function
          />
        </div>
      )}

      {/* NEW VERIFICATION POPUP */}
      <AadhaarVerificationPopup
        visible={isVerifyModalVisible}
        userPhoneNumber={formData.phoneNumber}
        onCancel={() => setIsVerifyModalVisible(false)}
        onSuccess={(data) => {
          setDocVerificationData(data);
          setIsVerifyModalVisible(false);
          setTermsAndConditionCheck(true);
        }}
      />
      {/* Org Search Modal */}
      {isOrgSearchModalVisible && (
        <div className="org-modal-overlay">
          {orgLoading ? (
            <div className="org-loading">Loading organizations...</div>
          ) : (
            <div className="org-modal-content">
              <h3>Select Organization</h3>
              <input
                type="text"
                placeholder="Search or type new..."
                value={orgSearchQuery}
                onChange={(e) => handleSearchOrg(e.target.value)}
              />
              <div className="org-list-container">
                {filteredOrgs.map((org, index) => (
                  <div
                    key={index}
                    className="org-list-item"
                    onClick={() => selectOrganization(org)}
                  >
                    {org}
                  </div>
                ))}
              </div>
              <div className="org-modal-footer">
                {orgSearchQuery.trim().length > 0 && (
                  <button
                    className="input-btn"
                    onClick={() => selectOrganization(orgSearchQuery)}
                  >
                    Add "{orgSearchQuery.trim()}"
                  </button>
                )}
                <button
                  className="input-btn"
                  style={{ background: "var(--color-text-muted)" }}
                  onClick={() => setIsOrgSearchModalVisible(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="card form-container">
        <div className="login-center">
          <img src={logo} alt="Logo" className="logo" />
          <h1>Smile2Steps Sign Up</h1>
          <div className="tabs">
            <p className="tab-left">
              <NavLink
                to={"/parent-signup"}
                className={({ isActive }) => (isActive ? "active-tab" : "")}
              >
                Parent
              </NavLink>
            </p>
            <p className="tab-right">
              <NavLink
                to={"/pediatrician-signup"}
                className={({ isActive }) => (isActive ? "active-tab" : "")}
              >
                Provider
              </NavLink>
            </p>
          </div>
        </div>
        <form noValidate onSubmit={handleSubmit}>
          <div className="grid-container">
            <div className="grid-item">
              <label>Phone Number *</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                autoComplete="phone_number"
              />
            </div>
            <div className="grid-item">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === " ") e.preventDefault();
                }}
                autoComplete="email"
              />
            </div>
            <div className="grid-item">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="first-name"
              />
            </div>
            <div className="grid-item">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="last-name"
              />
            </div>
            <div className="grid-item">
              <label>Organization Name *</label>
              <div
                onClick={() => setIsOrgSearchModalVisible(true)}
                className="search-modal-trigger"
              >
                {formData.clinicName || "Tap to search or enter organization"}
              </div>
            </div>

            <div className="grid-item">
              <label>Provider Type *</label>
              <select
                value={formData.specialization}
                onChange={handleChange}
                name="specialization"
                required
              >
                <option disabled value=""></option>
                <option value="pediatrician">Pediatrician</option>
                <option value="health_worker">Health worker</option>
              </select>
            </div>

            {/* CONDITIONAL PEDIATRICIAN INPUT */}
            {formData.specialization === "pediatrician" && (
              <div className="grid-item">
                <label>Medical Registration Number *</label>
                <input
                  type="text"
                  name="pedRegistrationNo"
                  value={formData.pedRegistrationNo}
                  onChange={handleChange}
                  placeholder="e.g., WBMC397015"
                />
              </div>
            )}

            <div style={{ position: "relative", width: "100%" }}>
              <div className="grid-item">
                <label>Password *</label>
                <input
                  type={!isPasswordVisible ? "password" : "text"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <FontAwesomeIcon
                  icon={isPasswordVisible ? faEyeSlash : faEye}
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "44px",
                    top: "56%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555",
                  }}
                />
              </div>
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <div className="grid-item">
                <label>Confirm Password *</label>
                <input
                  type={!isConfirmPasswordVisible ? "password" : "text"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <FontAwesomeIcon
                  icon={isConfirmPasswordVisible ? faEyeSlash : faEye}
                  onClick={() => setIsConfirmPasswordVisible((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "44px",
                    top: "56%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555",
                  }}
                />
              </div>
            </div>
          </div>

          <button
            disabled={
              !termsAndConditionCheck ||
              (formData.specialization === "health_worker" &&
                !docVerificationData)
            }
            type="submit"
            className={`${termsAndConditionCheck && !(formData.specialization === "health_worker" && !docVerificationData) ? "input-btn" : "disablebtn"}`}
          >
            Sign Up
          </button>

          {/* RECOVERY T&C LINK FOR HEALTH WORKERS */}
          <div className="t-and-c-container">
            <input
              type="checkbox"
              required
              checked={
                termsAndConditionCheck &&
                !(
                  formData.specialization === "health_worker" &&
                  !docVerificationData
                )
              }
              onChange={(e) => {
                handleTermsClick(e);
              }}
              disabled={
                (docSurveyCompleted &&
                  formData.specialization !== "health_worker") ||
                (docSurveyCompleted &&
                  formData.specialization === "health_worker" &&
                  !!docVerificationData) ||
                formData.phoneNumber.length !== 10
              }
            />
            {docSurveyCompleted &&
            formData.specialization === "health_worker" &&
            !docVerificationData ? (
              <span key="action-required" className="t-and-c">
                <Link
                  to="#"
                  onClick={handleTermsClick}
                  style={{ color: "red", fontWeight: "bold" }}
                >
                  ⚠ Action Required: Verify Identity
                </Link>
              </span>
            ) : (
              <span key="standard-terms" className="t-and-c">
                <span>I agree to the </span>
                <Link to="#" onClick={handleTermsClick}>
                  Terms & Conditions
                </Link>
                {docSurveyCompleted && <span> (Survey Completed)</span>}
              </span>
            )}
          </div>
        </form>

        <p className="login-navigation">
          Already have an account? Login (
          <span>
            <Link to="/parent-login">Parent</Link>
          </span>
          <b> / </b>
          <span>
            <Link to={"/pediatrician-login"}>Provider</Link>
          </span>
          )
        </p>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

export default PediatricianSignup;
